import {
  AudioOutlined,
  CameraOutlined,
  DeleteOutlined,
  EditOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  SettingOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  WechatOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { selectUser } from "../../redux/slices/auth.slice";
import { uploadDoc } from "../../services/upload.service";
import {
  addGroupMembers,
  createGroup,
  deleteGroup,
  fetchCampaign,
  fetchCampaigns,
  fetchClassParents,
  fetchGroup,
  fetchGroupPreview,
  fetchGroups,
  fetchMessageRecipients,
  fetchMessages,
  fetchSelectableClasses,
  fetchSelectableInstructors,
  removeGroupMember,
  resyncGroup,
  retryFailed,
  sendMessage,
  updateGroup,
  whatsappError,
} from "../../services/whatsapp.service";
import WhatsappTicks from "./WhatsappTicks";
import { whatsappAvatarColor, whatsappInitials } from "./whatsappAvatar";

const { Option } = Select;
const { TextArea } = Input;

const BODY_LIMIT = 1024;
const REPORT_PAGE_SIZE = 50;


const WA_GREEN = "#25D366";
const WA_TEAL = "#128C7E";
const WA_CHIP_BG = "#F0F2F5";
const TEXT_PRIMARY = "#111B21";
const TEXT_SECONDARY = "#667781";

/**
 * A plain flex-centered circle rather than antd's <Button icon={...}>.
 * Antd wraps the icon in its own `.ant-btn-icon` span with a margin meant to
 * separate it from label text — for an icon-only button that margin has
 * nothing to balance against, so the icon sits visibly off-center no matter
 * what flex/alignment is applied to the outer button. Rendering the icon
 * directly, with no antd wrapper in between, is what actually centers it.
 */
const IconCircle = React.forwardRef(
  (
    {
      icon: Icon,
      onClick,
      size = 36,
      iconSize,
      bg = WA_CHIP_BG,
      color = TEXT_SECONDARY,
      disabled = false,
      loading = false,
      title,
      // antd's <Dropdown> clones its trigger child to inject its own onClick
      // (to open the menu) plus assorted aria/class props — spreading and
      // preferring those here is what makes IconCircle work as a Dropdown
      // trigger, not just as a standalone button.
      ...rest
    },
    ref,
  ) => {
    const isInactive = disabled || loading;
    const effectiveOnClick = rest.onClick || onClick;
    const content = (
      <div
        {...rest}
        ref={ref}
        role="button"
        tabIndex={isInactive ? -1 : 0}
        onClick={isInactive ? undefined : effectiveOnClick}
        onKeyDown={(e) => !isInactive && e.key === "Enter" && effectiveOnClick?.(e)}
        style={{
          ...rest.style,
          width: size,
          height: size,
          minWidth: size,
          borderRadius: "50%",
          backgroundColor: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isInactive ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          flexShrink: 0,
        }}
      >
        {loading ? (
          <LoadingOutlined spin style={{ fontSize: iconSize || size * 0.45, color }} />
        ) : (
          <Icon style={{ fontSize: iconSize || size * 0.45, color }} />
        )}
      </div>
    );
    return title ? <Tooltip title={title}>{content}</Tooltip> : content;
  },
);

const ACCEPTED = {
  "image/jpeg": { type: "IMAGE", folder: "images" },
  "image/jpg": { type: "IMAGE", folder: "images" },
  "image/png": { type: "IMAGE", folder: "images" },
  "video/mp4": { type: "VIDEO", folder: "video" },
  "application/pdf": { type: "PDF", folder: "pdf" },
  "audio/mpeg": { type: "AUDIO", folder: "audio" },
  "audio/mp3": { type: "AUDIO", folder: "audio" },
};

const MEDIA_ICON = {
  IMAGE: <FileImageOutlined />,
  VIDEO: <VideoCameraOutlined />,
  PDF: <FilePdfOutlined />,
  AUDIO: <AudioOutlined />,
  VOICE: <AudioOutlined />,
};

const RECIPIENT_COLOR = {
  queued: "default",
  claimed: "processing",
  sent: "blue",
  delivered: "cyan",
  read: "green",
  failed: "red",
  skipped: "orange",
};

const lastMessagePreview = (msg) => {
  if (!msg) return "No messages yet";
  if (msg.body) return msg.body;
  const type = msg.media?.[0]?.type;
  if (type === "IMAGE") return "📷 Photo";
  if (type === "VIDEO") return "🎥 Video";
  if (type === "PDF") return "📄 Document";
  if (type === "AUDIO" || type === "VOICE") return "🎵 Audio";
  return "Message";
};

const formatDateHeader = (dateStr) => {
  const date = moment(dateStr);
  if (date.isSame(moment(), "day")) return "Today";
  if (date.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return date.format("D MMMM YYYY");
};


const WhatsAppChats = () => {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const isAdmin = user?.role === "admin";

  // ── list state ─────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const groups = useQuery(
    ["whatsapp/groups", debouncedSearch, typeFilter],
    () =>
      fetchGroups({
        search: debouncedSearch || undefined,
        type: typeFilter === "all" ? undefined : typeFilter,
      }),
  );
  const groupList = groups?.data?.items ?? [];
  const selectedGroup = groupList.find((g) => g._id === selectedGroupId) || null;

  // ── create-group modal (verbatim logic from the old Groups tab) ────────
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [groupType, setGroupType] = useState(isAdmin ? "instructor" : "parent");
  const [classId, setClassId] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createPhoto, setCreatePhoto] = useState(null);
  const [createPhotoUploading, setCreatePhotoUploading] = useState(false);
  const [isMarketing, setIsMarketing] = useState(false);
  const [campaignId, setCampaignId] = useState(null);

  const classes = useQuery("whatsapp/pickers/classes", fetchSelectableClasses, {
    enabled: createOpen && groupType === "parent",
  });
  const instructors = useQuery(
    "whatsapp/pickers/instructors",
    () => fetchSelectableInstructors(),
    { enabled: createOpen && groupType === "instructor" && isAdmin },
  );
  const classParents = useQuery(
    ["whatsapp/pickers/class-parents", classId],
    () => fetchClassParents(classId),
    { enabled: !!classId },
  );
  const campaigns = useQuery("whatsapp/campaigns", fetchCampaigns, {
    enabled: createOpen && isMarketing && isAdmin,
  });
  const campaignDetail = useQuery(
    ["whatsapp/campaign", campaignId],
    () => fetchCampaign(campaignId),
    {
      enabled: !!campaignId,
      onSuccess: (data) => {
        // Lets the campaign's name double as the group name, per the request
        // that "campaign group name need to display" — still editable.
        if (data?.name) createForm.setFieldsValue({ name: data.name });
      },
    },
  );

  const resetCreateForm = () => {
    createForm.resetFields();
    setClassId(null);
    setSelectedStudents([]);
    setSelectedInstructors([]);
    setGroupType(isAdmin ? "instructor" : "parent");
    setCreatePhoto(null);
    setIsMarketing(false);
    setCampaignId(null);
  };

  const handleMarketingToggle = (checked) => {
    setIsMarketing(checked);
    setCampaignId(null);
    setGroupType(checked ? "campaign" : isAdmin ? "instructor" : "parent");
  };

  const handleCreatePhotoSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Please choose an image file");
      return;
    }
    setCreatePhotoUploading(true);
    try {
      const res = await uploadDoc({ file }, "images");
      setCreatePhoto(res.files?.original);
    } catch (err) {
      message.error("Could not upload the photo");
    } finally {
      setCreatePhotoUploading(false);
    }
  };

  const handleTypeChange = (value) => {
    setGroupType(value);
    setClassId(null);
    setSelectedStudents([]);
    setSelectedInstructors([]);
  };

  const handleClassChange = (value) => {
    setClassId(value);
    setSelectedStudents([]);
  };

  const handleSelectAllStudents = () => {
    const students = classParents?.data?.students ?? [];
    const withParent = students.filter((s) => s.has_parent);
    const without = students.length - withParent.length;
    if (without > 0) {
      message.warning(`${without} student(s) skipped — no parent on record`);
    }
    setSelectedStudents(withParent.map((s) => s._id));
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const payload = {
        name: values.name,
        description: values.description || undefined,
        profile_image: createPhoto || undefined,
        type: groupType,
      };
      if (groupType === "instructor") {
        if (!selectedInstructors.length) {
          message.error("Select at least one instructor");
          return;
        }
        payload.user_ids = selectedInstructors;
      } else if (groupType === "campaign") {
        if (!campaignId) {
          message.error("Select a campaign");
          return;
        }
        payload.campaign_id = campaignId;
      } else {
        if (!classId) {
          message.error("Select a class");
          return;
        }
        payload.class_ids = [classId];
        if (selectedStudents.length) payload.student_ids = selectedStudents;
      }

      const result = await createGroup(payload);
      message.success(result?.message || "Group created");
      setCreateOpen(false);
      resetCreateForm();
      queryClient.invalidateQueries("whatsapp/groups");
      if (result?.data?._id) setSelectedGroupId(result.data._id);
    } catch (err) {
      message.error(whatsappError(err, "Could not create the group"));
    } finally {
      setCreating(false);
    }
  };

  // ── members modal (opened from the thread header) ──────────────────────
  const [membersGroupId, setMembersGroupId] = useState(null);
  const memberDetail = useQuery(
    ["whatsapp/group", membersGroupId],
    () => fetchGroup(membersGroupId),
    { enabled: !!membersGroupId },
  );

  const handleRemoveMember = async (userId) => {
    try {
      await removeGroupMember(membersGroupId, userId);
      message.success("Member removed");
      queryClient.invalidateQueries(["whatsapp/group", membersGroupId]);
      queryClient.invalidateQueries("whatsapp/groups");
    } catch (err) {
      message.error(whatsappError(err, "Could not remove the member"));
    }
  };

  // ── add member (within the members modal) ───────────────────────────────
  const membersGroupType = memberDetail?.data?.group?.type;
  const [addInstructorIds, setAddInstructorIds] = useState([]);
  const [addClassId, setAddClassId] = useState(null);
  const [addStudentIds, setAddStudentIds] = useState([]);
  const [addingMember, setAddingMember] = useState(false);
  const [addNumberInput, setAddNumberInput] = useState("");
  const [addNumbersList, setAddNumbersList] = useState([]);

  const addInstructors = useQuery(
    "whatsapp/pickers/instructors-add",
    () => fetchSelectableInstructors(),
    { enabled: !!membersGroupId && membersGroupType === "instructor" },
  );
  const addClasses = useQuery(
    "whatsapp/pickers/classes-add",
    fetchSelectableClasses,
    { enabled: !!membersGroupId && membersGroupType === "parent" },
  );
  const addClassParents = useQuery(
    ["whatsapp/pickers/class-parents-add", addClassId],
    () => fetchClassParents(addClassId),
    { enabled: !!addClassId },
  );

  const resetAddMemberForm = () => {
    setAddInstructorIds([]);
    setAddClassId(null);
    setAddStudentIds([]);
    setAddNumberInput("");
    setAddNumbersList([]);
  };

  const addNumberToPendingList = () => {
    const value = addNumberInput.trim();
    if (!value) return;
    if (addNumbersList.includes(value)) {
      message.warning("That number is already in the list");
      setAddNumberInput("");
      return;
    }
    setAddNumbersList((prev) => [...prev, value]);
    setAddNumberInput("");
  };

  const removeNumberFromPendingList = (value) => {
    setAddNumbersList((prev) => prev.filter((n) => n !== value));
  };

  const handleAddMembers = async () => {
    if (!membersGroupId) return;

    if (membersGroupType === "campaign") {
      if (!addNumbersList.length) {
        message.warning("Add at least one phone number");
        return;
      }
      setAddingMember(true);
      try {
        const result = await addGroupMembers(membersGroupId, {
          numbers_text: addNumbersList.join("\n"),
        });
        message.success(`Added ${result?.added ?? 0} number(s)`);
        setAddNumbersList([]);
        setAddNumberInput("");
        queryClient.invalidateQueries(["whatsapp/group", membersGroupId]);
        queryClient.invalidateQueries("whatsapp/groups");
      } catch (err) {
        message.error(whatsappError(err, "Could not add number(s)"));
      } finally {
        setAddingMember(false);
      }
      return;
    }

    const payload =
      membersGroupType === "instructor"
        ? { user_ids: addInstructorIds }
        : { class_ids: addClassId ? [addClassId] : [], student_ids: addStudentIds };

    if (
      (membersGroupType === "instructor" && !addInstructorIds.length) ||
      (membersGroupType === "parent" && !addClassId)
    ) {
      message.warning("Select at least one member to add");
      return;
    }

    setAddingMember(true);
    try {
      const result = await addGroupMembers(membersGroupId, payload);
      message.success(`Added ${result?.added ?? 0} member(s)`);
      resetAddMemberForm();
      queryClient.invalidateQueries(["whatsapp/group", membersGroupId]);
      queryClient.invalidateQueries("whatsapp/groups");
    } catch (err) {
      message.error(whatsappError(err, "Could not add member(s)"));
    } finally {
      setAddingMember(false);
    }
  };

  // ── group-level actions (resync / delete), from the thread header menu ─
  const handleResync = async () => {
    if (!selectedGroup) return;
    try {
      const result = await resyncGroup(selectedGroup._id);
      message.success(`Resynced — ${result.added} added, ${result.removed} removed`);
      queryClient.invalidateQueries("whatsapp/groups");
    } catch (err) {
      message.error(whatsappError(err, "Could not resync the group"));
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    try {
      await deleteGroup(selectedGroup._id);
      message.success("Group deleted");
      setSelectedGroupId(null);
      queryClient.invalidateQueries("whatsapp/groups");
    } catch (err) {
      message.error(whatsappError(err, "Could not delete the group"));
    }
  };

  // ── edit group: name, description, photo ────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const openEditGroup = () => {
    if (!selectedGroup) return;
    editForm.setFieldsValue({
      name: selectedGroup.name,
      description: selectedGroup.description,
    });
    setEditPhoto(selectedGroup.profile_image || null);
    setEditOpen(true);
  };

  const handleEditPhotoSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Please choose an image file");
      return;
    }
    setEditPhotoUploading(true);
    try {
      const res = await uploadDoc({ file }, "images");
      setEditPhoto(res.files?.original);
    } catch (err) {
      message.error("Could not upload the photo");
    } finally {
      setEditPhotoUploading(false);
    }
  };

  const handleEditSave = async (values) => {
    if (!selectedGroup) return;
    setEditSaving(true);
    try {
      await updateGroup(selectedGroup._id, {
        name: values.name,
        description: values.description || "",
        // '' explicitly clears the photo — matches the backend's contract
        // (omitting the field would leave it untouched instead).
        profile_image: editPhoto || "",
      });
      message.success("Group updated");
      setEditOpen(false);
      queryClient.invalidateQueries("whatsapp/groups");
    } catch (err) {
      message.error(whatsappError(err, "Could not update the group"));
    } finally {
      setEditSaving(false);
    }
  };

  // ── open chat: messages + cap preview ───────────────────────────────────
  const messagesQuery = useQuery(
    ["whatsapp/messages", selectedGroupId],
    () => fetchMessages({ group_id: selectedGroupId }),
    { enabled: !!selectedGroupId, refetchInterval: 10000 },
  );
  const threadMessages = useMemo(
    () => [...(messagesQuery?.data?.items ?? [])].reverse(),
    [messagesQuery?.data],
  );

  const preview = useQuery(
    ["whatsapp/group-preview", selectedGroupId],
    () => fetchGroupPreview(selectedGroupId),
    { enabled: !!selectedGroupId },
  );

  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [threadMessages.length, selectedGroupId]);

  // ── composer ─────────────────────────────────────────────────────────
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const kind = ACCEPTED[file.type];
    if (!kind) {
      message.error("WhatsApp accepts JPG, PNG, MP4, MP3 or PDF attachments only.");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadDoc({ file }, kind.folder);
      setAttachment({
        id: res.id,
        name: res.files?.name || file.name,
        type: kind.type,
        url: res.files?.original,
      });
    } catch (err) {
      message.error("Could not upload the attachment");
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedGroupId || !body.trim()) return;
    setSending(true);
    try {
      const result = await sendMessage({
        group_id: selectedGroupId,
        body: body.trim(),
        media: attachment ? [attachment] : undefined,
      });
      if (result?.data?.warning) message.warning(result.data.warning);
      setBody("");
      setAttachment(null);
      queryClient.invalidateQueries(["whatsapp/messages", selectedGroupId]);
      queryClient.invalidateQueries("whatsapp/groups");
    } catch (err) {
      message.error(whatsappError(err, "Could not send the message"));
    } finally {
      setSending(false);
    }
  };

  // ── delivery report + retry/cancel ──────────────────────────────────────
  const [reportId, setReportId] = useState(null);
  const [reportPage, setReportPage] = useState(1);

  const report = useQuery(
    ["whatsapp/message-recipients", reportId, reportPage],
    () =>
      fetchMessageRecipients(reportId, {
        skip: (reportPage - 1) * REPORT_PAGE_SIZE,
        limit: REPORT_PAGE_SIZE,
      }),
    { enabled: !!reportId, refetchInterval: 10000, keepPreviousData: true },
  );

  const openReport = (id) => {
    setReportPage(1);
    setReportId(id);
  };

  const handleRetry = async (id) => {
    try {
      const result = await retryFailed(id);
      if (result?.requeued > 0) {
        message.success(`Requeued ${result.requeued} recipient(s)`);
      } else {
        message.info(
          "Nothing to retry. Failures that already reached WhatsApp are not resent, to avoid delivering twice.",
        );
      }
      queryClient.invalidateQueries(["whatsapp/messages", selectedGroupId]);
    } catch (err) {
      message.error(whatsappError(err, "Could not retry"));
    }
  };

  const InitialsCircle = ({ name, size }) => (
    <div
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "50%",
        backgroundColor: whatsappAvatarColor(name || "?"),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 600,
        fontSize: Math.round(size * 0.4),
        flexShrink: 0,
      }}
    >
      {whatsappInitials(name || "?")}
    </div>
  );

  const renderAvatar = (group, size = 44) =>
    group?.profile_image ? (
      <Avatar size={size} src={group.profile_image} style={{ flexShrink: 0 }} />
    ) : (
      <InitialsCircle name={group?.name} size={size} />
    );

  const students = classParents?.data?.students ?? [];
  const memberColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Phone",
      key: "phone",
      render: (record) => record.phone || record.phone_raw || "—",
    },
    {
      title: "Reachable",
      key: "reachable",
      render: (record) =>
        record.reachable ? (
          <span>
            <Tag color="green">Yes</Tag>
            {record.shares_number_with > 0 && (
              <Tooltip
                title={`Shares a number with ${record.shares_number_with} other member(s) — one message is sent, not several`}
              >
                <Tag className="ml-2">shared</Tag>
              </Tooltip>
            )}
          </span>
        ) : (
          <Tag color="orange">
            {record.reason === "INVALID_PHONE" ? "Number is not a valid mobile" : "No number on record"}
          </Tag>
        ),
    },
    {
      title: "",
      key: "remove",
      align: "center",
      render: (record) => (
        <Popconfirm
          title="Remove from group?"
          onConfirm={() => handleRemoveMember(record.user_id || record.phone_raw)}
          okText="Yes"
          cancelText="No"
        >
          <Button size="small" danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const recipientColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Number", dataIndex: "phone", key: "phone" },
    {
      title: "Status",
      key: "status",
      render: (record) => <Tag color={RECIPIENT_COLOR[record.status] || "default"}>{record.status}</Tag>,
    },
    {
      title: "Detail",
      key: "detail",
      render: (record) =>
        record.error_message ? (
          <Tooltip title={record.error_message}>
            <span className="text-xs text-red-500">{record.error_message}</span>
          </Tooltip>
        ) : (
          <span className="text-xs text-gray-500">
            {record.read_at
              ? `Read ${moment(record.read_at).format("DD-MM HH:mm")}`
              : record.delivered_at
              ? `Delivered ${moment(record.delivered_at).format("DD-MM HH:mm")}`
              : record.sent_at
              ? `Sent ${moment(record.sent_at).format("DD-MM HH:mm")}`
              : "—"}
          </span>
        ),
    },
  ];

  const stats = preview?.data;

  return (
    <div className="mt-8">
      <div
        className="wa-chat-shell bg-white rounded-lg drop-shadow-md flex"
        style={{ height: "calc(100vh - 220px)", minHeight: 520, overflow: "hidden", position: "relative" }}
      >
        {/* ── Chat list ─────────────────────────────────────────────── */}
        <div style={{ width: 360, borderRight: "1px solid #f0f0f0", display: "flex", flexDirection: "column" }}>
          <div className="flex justify-between items-center p-4">
            <div className="text-xl font-semibold" style={{ color: TEXT_PRIMARY }}>Chats</div>
            <IconCircle
              icon={PlusOutlined}
              title="New group"
              onClick={() => {
                resetCreateForm();
                setCreateOpen(true);
              }}
            />
          </div>

          <div className="px-4 pb-3">
            <Input
              prefix={<SearchOutlined style={{ color: TEXT_SECONDARY }} />}
              placeholder="Search chats"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ borderRadius: 8, backgroundColor: WA_CHIP_BG, border: "none" }}
            />
          </div>

          {/* Repurposes WhatsApp's own All/Unread/Favourites row for the
              filter that actually applies to a distribution list. */}
          <div className="px-4 pb-3 flex" style={{ gap: 10 }}>
            {[
              { key: "all", label: "All" },
              { key: "parent", label: "Parents" },
              // Instructors can never own an instructor-type group (creating
              // one is admin-only), so this chip would only ever show "no
              // groups" for them — hide it rather than offer a dead filter.
              ...(isAdmin ? [{ key: "instructor", label: "Instructors" }] : []),
            ].map((f) => {
              const active = typeFilter === f.key;
              return (
                <div
                  key={f.key}
                  onClick={() => setTypeFilter(f.key)}
                  style={{
                    padding: "5px 16px",
                    borderRadius: 16,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    userSelect: "none",
                    backgroundColor: active ? WA_TEAL : WA_CHIP_BG,
                    color: active ? "#fff" : TEXT_PRIMARY,
                  }}
                >
                  {f.label}
                </div>
              );
            })}
          </div>

          <div style={{ flex: 1, overflowY: "auto" }}>
            {groups?.isLoading ? (
              <div className="p-6 text-center"><Spin /></div>
            ) : groupList.length === 0 ? (
              <Empty className="pt-10" description="No groups yet. Create one to start messaging." />
            ) : (
              groupList.map((g) => (
                <div
                  key={g._id}
                  onClick={() => setSelectedGroupId(g._id)}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                  style={{
                    backgroundColor: selectedGroupId === g._id ? "#f0f2f5" : "transparent",
                    borderBottom: "1px solid #f5f5f5",
                  }}
                >
                  {renderAvatar(g)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex justify-between items-baseline">
                      <div className="truncate" style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{g.name}</div>
                      {g.last_message?.createdAt && (
                        <div className="whitespace-nowrap ml-2" style={{ fontSize: 11, color: TEXT_SECONDARY }}>
                          {moment(g.last_message.createdAt).format("DD/MM")}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 truncate" style={{ fontSize: 13, color: TEXT_SECONDARY }}>
                      {g.last_message && <WhatsappTicks counts={g.last_message.counts} size={12} />}
                      <span className="truncate">{lastMessagePreview(g.last_message)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Open chat ─────────────────────────────────────────────── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {!selectedGroup ? (
            <div className="flex flex-col items-center justify-center h-full" style={{ color: TEXT_SECONDARY }}>
              <WechatOutlined style={{ fontSize: 64, color: WA_TEAL, opacity: 0.5 }} />
              <div className="mt-3 text-lg">Select a chat to start messaging</div>
              <Alert
                className="mt-6"
                style={{ maxWidth: 560, padding: "14px 20px" }}
                type="info"
                showIcon
                message="Each member receives their own private WhatsApp message from the school's number. This is a distribution list, not a group chat — members cannot see one another."
              />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #f0f0f0" }}>
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => setMembersGroupId(selectedGroup._id)}
                >
                  {renderAvatar(selectedGroup, 40)}
                  <div>
                    <div style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{selectedGroup.name}</div>
                    <div style={{ fontSize: 12, color: TEXT_SECONDARY }}>
                      {selectedGroup.reachable_count ?? 0} / {selectedGroup.member_count ?? 0} reachable
                    </div>
                  </div>
                </div>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "members",
                        icon: <TeamOutlined />,
                        label: "View members",
                        onClick: () => setMembersGroupId(selectedGroup._id),
                      },
                      { key: "edit", icon: <EditOutlined />, label: "Edit group info", onClick: openEditGroup },
                      ...(selectedGroup.type === "parent" && selectedGroup.source_class_ids?.length > 0
                        ? [{ key: "resync", icon: <ReloadOutlined />, label: "Resync from classes", onClick: handleResync }]
                        : []),
                      {
                        key: "delete",
                        icon: <DeleteOutlined />,
                        danger: true,
                        label: (
                          <Popconfirm title="Delete this group?" onConfirm={handleDeleteGroup} okText="Yes" cancelText="No">
                            Delete group
                          </Popconfirm>
                        ),
                      },
                    ],
                  }}
                  trigger={["click"]}
                  placement="bottomRight"
                  getPopupContainer={(trigger) => trigger.closest(".wa-chat-shell") || trigger.parentNode}
                >
                  <Button icon={<SettingOutlined />}>Settings</Button>
                </Dropdown>
              </div>

              {/* Pinned — stays put regardless of scroll position, unlike the
                  per-day date pills inside the scrollable list below. */}
              <div className="flex justify-center py-2" style={{ backgroundColor: "#F7F8FA", borderBottom: "1px solid #f0f0f0" }}>
                <div
                  className="flex items-center gap-1"
                  style={{
                    background: "#FFF3CD",
                    color: "#8A6D1F",
                    fontSize: 11.5,
                    fontWeight: 500,
                    padding: "6px 14px",
                    borderRadius: 8,
                    textAlign: "center",
                    maxWidth: "90%",
                  }}
                >
                  <InfoCircleOutlined />
                  Once sent, a message cannot be edited or deleted.
                </div>
              </div>

              <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "16px", backgroundColor: "#F7F8FA" }}>
                {messagesQuery?.isLoading ? (
                  <div className="text-center pt-10"><Spin /></div>
                ) : threadMessages.length === 0 ? (
                  <Empty description="No messages yet" className="pt-10" />
                ) : (
                  threadMessages.map((m, index) => {
                    const media = m.media?.[0];
                    const showDateHeader =
                      index === 0 ||
                      !moment(m.createdAt).isSame(moment(threadMessages[index - 1].createdAt), "day");
                    return (
                      <React.Fragment key={m._id}>
                        {showDateHeader && (
                          <div className="flex justify-center mb-3">
                            <div
                              style={{
                                background: "#E9EDEF",
                                color: "#54656F",
                                fontSize: 12,
                                fontWeight: 600,
                                padding: "5px 12px",
                                borderRadius: 8,
                              }}
                            >
                              {formatDateHeader(m.createdAt)}
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end mb-3">
                        <div
                          onClick={() => openReport(m._id)}
                          style={{
                            maxWidth: "70%",
                            backgroundColor: m.status === "failed" ? "#fff1f0" : "#DCF8C6",
                            borderRadius: 8,
                            padding: "8px 10px",
                            cursor: "pointer",
                            boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
                          }}
                        >
                          {media && (media.type === "IMAGE" || media.type === "VIDEO") && (
                            <div
                              className="mb-1"
                              onClick={(e) => e.stopPropagation()}
                              style={{ cursor: "default" }}
                            >
                              {media.type === "IMAGE" ? (
                                <img
                                  src={media.url}
                                  alt={media.name || "Image"}
                                  style={{
                                    display: "block",
                                    maxWidth: "100%",
                                    maxHeight: 260,
                                    borderRadius: 6,
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <video
                                  src={media.url}
                                  controls
                                  style={{ display: "block", maxWidth: "100%", maxHeight: 260, borderRadius: 6 }}
                                />
                              )}
                            </div>
                          )}
                          {media && media.type !== "IMAGE" && media.type !== "VIDEO" && (
                            <div className="flex items-center gap-2 mb-1 px-2 py-1 rounded bg-white bg-opacity-60 text-xs">
                              {MEDIA_ICON[media.type]}
                              <span className="truncate">{media.name || media.type}</span>
                            </div>
                          )}
                          <div className="text-sm">{m.body}</div>
                          {m.status === "failed" && m.failure_reason && (
                            <div className="text-xs text-red-600 mt-1">{m.failure_reason}</div>
                          )}
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-xs text-gray-500">{moment(m.createdAt).format("h:mm A")}</span>
                            <WhatsappTicks counts={m.counts} />
                            <Tooltip title="Tap for delivery details">
                              <InfoCircleOutlined style={{ fontSize: 11, color: "rgba(0,0,0,0.35)" }} />
                            </Tooltip>
                          </div>
                          {(m.counts?.failed ?? 0) > 0 && (
                            <div className="flex justify-end mt-1" onClick={(e) => e.stopPropagation()}>
                              <span
                                role="button"
                                tabIndex={0}
                                className="text-xs cursor-pointer"
                                style={{ color: WA_TEAL }}
                                onClick={() => handleRetry(m._id)}
                                onKeyDown={(e) => e.key === "Enter" && handleRetry(m._id)}
                              >
                                Retry
                              </span>
                            </div>
                          )}
                        </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
              </div>

              <div style={{ borderTop: "1px solid #f0f0f0", padding: "12px 16px" }}>
                {stats &&
                  (stats.unreachable?.length > 0 ||
                    stats.exceeds_daily_cap ||
                    stats.exceeds_wallet_balance) && (
                    <Alert
                      className="mb-2"
                      type="warning"
                      showIcon
                      message={
                        stats.exceeds_wallet_balance
                          ? `Your WhatsApp wallet balance (₹${(stats.wallet_balance_paise / 100).toFixed(2)}) is too low to send to all ${stats.reachable} recipient(s) at ₹${((stats.unit_cost_paise ?? 15) / 100).toFixed(2)} each. Recharge in Settings.`
                          : stats.exceeds_daily_cap
                          ? `This exceeds your WhatsApp limit of ${stats.daily_conversation_cap} conversations per 24 hours.`
                          : `${stats.unreachable.length} member(s) have no valid WhatsApp number and will be skipped.`
                      }
                    />
                  )}
                {attachment && (
                  <Tag
                    closable
                    onClose={() => setAttachment(null)}
                    color="blue"
                    style={{ marginBottom: 8, padding: "4px 8px" }}
                  >
                    {MEDIA_ICON[attachment.type]} {attachment.name}
                  </Tag>
                )}
                <div className="flex items-center" style={{ gap: 12 }}>
                  <IconCircle
                    icon={PaperClipOutlined}
                    loading={uploading}
                    disabled={!!attachment}
                    onClick={() => document.getElementById("whatsappChatFileInput").click()}
                  />
                  <input
                    id="whatsappChatFileInput"
                    type="file"
                    style={{ display: "none" }}
                    accept=".jpg,.jpeg,.png,.mp4,.mp3,.pdf"
                    onChange={handleFileSelect}
                  />
                  <TextArea
                    autoSize={{ minRows: 1, maxRows: 5 }}
                    maxLength={BODY_LIMIT}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Type a message"
                    style={{
                      borderRadius: 20,
                      resize: "none",
                      backgroundColor: WA_CHIP_BG,
                      border: "none",
                      padding: "8px 16px",
                    }}
                  />
                  <IconCircle
                    icon={SendOutlined}
                    loading={sending}
                    disabled={!body.trim() || uploading}
                    onClick={handleSend}
                    bg={body.trim() && !uploading ? WA_GREEN : WA_CHIP_BG}
                    color={body.trim() && !uploading ? "#fff" : TEXT_SECONDARY}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── create group ───────────────────────────────────────────── */}
      <Modal
        width={720}
        visible={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          resetCreateForm();
        }}
        footer={null}
        destroyOnClose
        title="Create WhatsApp Group"
      >
        <div className="flex flex-col items-center pb-4">
          <div style={{ position: "relative", width: 88, height: 88 }}>
            {createPhoto ? (
              <Avatar size={88} src={createPhoto} style={{ flexShrink: 0 }} />
            ) : (
              <div
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: "50%",
                  backgroundColor: WA_CHIP_BG,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FileImageOutlined style={{ fontSize: 28, color: TEXT_SECONDARY }} />
              </div>
            )}
            <Button
              shape="circle"
              size="small"
              type="primary"
              icon={createPhotoUploading ? <LoadingOutlined /> : <CameraOutlined />}
              style={{ position: "absolute", bottom: 0, right: 0, backgroundColor: WA_TEAL, borderColor: WA_TEAL }}
              onClick={() => document.getElementById("createGroupPhotoInput").click()}
            />
            <input
              id="createGroupPhotoInput"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleCreatePhotoSelect}
            />
          </div>
          {createPhoto && (
            <span
              role="button"
              tabIndex={0}
              className="text-xs mt-2 cursor-pointer"
              style={{ color: "#cf1322" }}
              onClick={() => setCreatePhoto(null)}
              onKeyDown={(e) => e.key === "Enter" && setCreatePhoto(null)}
            >
              Remove photo
            </span>
          )}
        </div>

        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item label="Group Name" name="name" rules={[{ required: true, message: "Group name is required" }]}>
            <Input placeholder="e.g. Grade 5 Parents" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input placeholder="Optional" />
          </Form.Item>

          {isAdmin && (
            <Form.Item>
              <Checkbox
                checked={isMarketing}
                onChange={(e) => handleMarketingToggle(e.target.checked)}
              >
                Marketing — build this group from a saved Campaign's phone numbers
              </Checkbox>
            </Form.Item>
          )}

          {!isMarketing && (
            <Form.Item label="Send to">
              <Select value={groupType} onChange={handleTypeChange}>
                {isAdmin && <Option value="instructor">Instructors</Option>}
                <Option value="parent">Parents</Option>
              </Select>
            </Form.Item>
          )}

          {isMarketing ? (
            <>
              <Form.Item label="Campaign">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a campaign"
                  value={campaignId}
                  onChange={setCampaignId}
                  loading={campaigns?.isLoading}
                  optionFilterProp="children"
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {(campaigns?.data?.items ?? []).map((c) => (
                    <Option key={c._id} value={c._id}>
                      {c.name} ({c.number_count} number{c.number_count === 1 ? "" : "s"})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {campaignId && (
                <Form.Item label="Numbers in this campaign">
                  <div
                    style={{
                      maxHeight: 160,
                      overflowY: "auto",
                      border: "1px solid #f0f0f0",
                      borderRadius: 6,
                      padding: 8,
                    }}
                  >
                    {campaignDetail?.isLoading ? (
                      <Spin size="small" />
                    ) : (
                      (campaignDetail?.data?.numbers ?? []).map((n) => (
                        <Tag key={n} style={{ margin: 4 }}>
                          {n}
                        </Tag>
                      ))
                    )}
                  </div>
                </Form.Item>
              )}
            </>
          ) : groupType === "instructor" ? (
            <Form.Item label="Select Instructors">
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Select instructors"
                value={selectedInstructors}
                onChange={setSelectedInstructors}
                loading={instructors?.isLoading}
                optionFilterProp="children"
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {(instructors?.data ?? []).map((i) => (
                  <Option key={i.user_id} value={i.user_id}>
                    {i.name}
                    {i.reachable ? "" : " — no WhatsApp number"}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: classId ? "1fr 1fr" : "1fr", gap: 16 }}>
              <Form.Item label="Class">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select class"
                  value={classId}
                  onChange={handleClassChange}
                  loading={classes?.isLoading}
                  optionFilterProp="children"
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {(classes?.data ?? []).map((c) => (
                    <Option key={c._id} value={c._id}>
                      {c.name} ({c.student_count})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {classId && (
                <Form.Item
                  label={
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <span>Students (leave empty for all)</span>
                      <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={handleSelectAllStudents}>
                        Select All
                      </Button>
                    </div>
                  }
                >
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="All students in the class"
                    value={selectedStudents}
                    onChange={setSelectedStudents}
                    loading={classParents?.isLoading}
                    optionFilterProp="children"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    {students.map((s) => (
                      <Option key={s._id} value={s._id} disabled={!s.has_parent}>
                        {s.name}
                        {s.has_parent ? "" : " — no parent linked"}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </div>
          )}

          {classId && classParents?.data?.students_without_parent > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${classParents.data.students_without_parent} student(s) have no parent linked.`}
            />
          )}

          <div className="flex justify-center items-center py-5">
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              loading={creating}
              style={{ backgroundColor: WA_GREEN, borderColor: WA_GREEN }}
            >
              Create Group
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── edit group info ─────────────────────────────────────────── */}
      <Modal
        width={480}
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={null}
        destroyOnClose
        title="Edit Group Info"
      >
        <div className="flex flex-col items-center pb-4">
          <div style={{ position: "relative", width: 88, height: 88 }}>
            {editPhoto ? (
              <Avatar size={88} src={editPhoto} style={{ flexShrink: 0 }} />
            ) : (
              <InitialsCircle name={selectedGroup?.name} size={88} />
            )}
            <Button
              shape="circle"
              size="small"
              type="primary"
              icon={editPhotoUploading ? <LoadingOutlined /> : <CameraOutlined />}
              style={{ position: "absolute", bottom: 0, right: 0, backgroundColor: WA_TEAL, borderColor: WA_TEAL }}
              onClick={() => document.getElementById("groupPhotoInput").click()}
            />
            <input
              id="groupPhotoInput"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleEditPhotoSelect}
            />
          </div>
          {editPhoto && (
            <span
              role="button"
              tabIndex={0}
              className="text-xs mt-2 cursor-pointer"
              style={{ color: "#cf1322" }}
              onClick={() => setEditPhoto(null)}
              onKeyDown={(e) => e.key === "Enter" && setEditPhoto(null)}
            >
              Remove photo
            </span>
          )}
        </div>

        <Form form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item label="Group Name" name="name" rules={[{ required: true, message: "Group name is required" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          <div className="flex justify-center pt-2">
            <Button
              type="primary"
              htmlType="submit"
              loading={editSaving}
              style={{ backgroundColor: WA_GREEN, borderColor: WA_GREEN }}
            >
              Save
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── members ─────────────────────────────────────────────────── */}
      <Modal
        width={860}
        visible={!!membersGroupId}
        onCancel={() => {
          setMembersGroupId(null);
          resetAddMemberForm();
        }}
        footer={null}
        destroyOnClose
        title={memberDetail?.data?.group?.name || "Group Members"}
      >
        <div style={{ marginBottom: 16, padding: 12, background: "#F7F8FA", borderRadius: 8 }}>
          <div className="text-sm text-gray-500 mb-2">Add member</div>
          {membersGroupType === "campaign" ? (
            <div>
              <div style={{ display: "flex", gap: 12 }}>
                <Input
                  placeholder="Enter a phone number"
                  value={addNumberInput}
                  onChange={(e) => setAddNumberInput(e.target.value)}
                  onPressEnter={addNumberToPendingList}
                  style={{ flex: 1 }}
                />
                <Button onClick={addNumberToPendingList}>Add to list</Button>
                <Button
                  type="primary"
                  loading={addingMember}
                  onClick={handleAddMembers}
                  disabled={!addNumbersList.length}
                >
                  Save to group
                </Button>
              </div>
              {addNumbersList.length > 0 && (
                <div className="mt-2">
                  {addNumbersList.map((n) => (
                    <Tag
                      key={n}
                      closable
                      onClose={() => removeNumberFromPendingList(n)}
                      style={{
                        marginBottom: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        lineHeight: "normal",
                      }}
                    >
                      {n}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          ) : membersGroupType === "instructor" ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Select
                mode="multiple"
                showSearch
                allowClear
                style={{ flex: 1 }}
                placeholder="Select instructor(s) to add"
                value={addInstructorIds}
                onChange={setAddInstructorIds}
                loading={addInstructors?.isLoading}
                optionFilterProp="children"
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {(addInstructors?.data ?? [])
                  .filter(
                    (i) =>
                      !(memberDetail?.data?.members ?? []).some((m) => m.user_id === i.user_id),
                  )
                  .map((i) => (
                    <Option key={i.user_id} value={i.user_id}>
                      {i.name}
                      {i.reachable ? "" : " — no WhatsApp number"}
                    </Option>
                  ))}
              </Select>
              <Button type="primary" loading={addingMember} onClick={handleAddMembers}>
                Add
              </Button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Select
                showSearch
                allowClear
                style={{ minWidth: 180 }}
                placeholder="Select class"
                value={addClassId}
                onChange={(v) => {
                  setAddClassId(v);
                  setAddStudentIds([]);
                }}
                loading={addClasses?.isLoading}
                optionFilterProp="children"
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {(addClasses?.data ?? []).map((c) => (
                  <Option key={c._id} value={c._id}>
                    {c.name} ({c.student_count})
                  </Option>
                ))}
              </Select>
              {addClassId && (
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  style={{ flex: 1, minWidth: 220 }}
                  placeholder="All students in the class (or pick specific ones)"
                  value={addStudentIds}
                  onChange={setAddStudentIds}
                  loading={addClassParents?.isLoading}
                  optionFilterProp="children"
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {(addClassParents?.data?.students ?? []).map((s) => (
                    <Option key={s._id} value={s._id} disabled={!s.has_parent}>
                      {s.name}
                      {s.has_parent ? "" : " — no parent linked"}
                    </Option>
                  ))}
                </Select>
              )}
              <Button type="primary" loading={addingMember} onClick={handleAddMembers}>
                Add
              </Button>
            </div>
          )}
        </div>

        <Table
          rowKey={(record) => record.user_id || record.phone_raw}
          size="small"
          columns={memberColumns}
          dataSource={memberDetail?.data?.members ?? []}
          loading={memberDetail?.isLoading}
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      {/* ── delivery report ─────────────────────────────────────────── */}
      <Modal
        width={900}
        visible={!!reportId}
        onCancel={() => setReportId(null)}
        footer={null}
        destroyOnClose
        title="Delivery Report"
      >
        <Table
          rowKey="_id"
          size="small"
          columns={recipientColumns}
          dataSource={report?.data?.items ?? []}
          loading={report?.isLoading}
          pagination={{
            current: reportPage,
            pageSize: REPORT_PAGE_SIZE,
            total: report?.data?.total ?? 0,
            onChange: (page) => setReportPage(page),
          }}
        />
      </Modal>
    </div>
  );
};

export default WhatsAppChats;
